import type { Route } from 'vue-router'
import { getInputFieldDefaultValue } from './getInputFieldDefaultValue'
import type {
  IEntityMaskData,
  TEntityMaskItem,
  IEntityParentObjectForCreate,
  TEntityMaskDataValueTypes,
} from '~/types/models'

const fn = '[cynqueModules/getClearFieldValues] getClearFieldValue |'

/**
 * The value a field is reset to, decided from its `start` alone.
 *
 * ⚠ SYNCHRONOUS, AND THAT IS WHY IT IS SEPARATE. `getClearFieldValue` below is
 * async only because of the disabled-with-a-default branch, which re-applies a
 * default and may await a `default()` function. The clearOtherOnChanged /
 * clearOtherOnCleared directives in EntityMaskForm run inside `selectChanged`,
 * `autocompleteChanged` and `comboboxChanged` -- synchronous `@update:model-value`
 * handlers -- so they cannot await. They call THIS, and get the identical
 * branch table rather than a second copy of it that could drift.
 *
 * Clearing a sibling never re-applies its default, so no branch is lost here.
 */
export function getClearFieldValueFromStart(
  ifield: IEntityMaskData
): TEntityMaskDataValueTypes {
  // A list edited over a stored string is empty as [], never null: the widget
  // expects an array, and [] equals the create-time `start` so a cleared field
  // stays clean rather than sending an empty value.
  if (ifield.delimitedStringSeparator !== undefined) {
    return []
  }
  // ⚠ THE FORCE-DIRTY IDIOM: `start: undefined` + `default: null` means "always
  // send this property, as null, unless the user picks something". It works by
  // inequality -- the default is applied at init so `value` becomes null while
  // `start` stays undefined, and isDirtyIField marks the field dirty because
  // `!isEqual(start, value)`.
  //
  // Clearing such a field back to `undefined` silently undid that: the field
  // stopped being dirty and the property vanished from the request. That is how
  // a Case POST came to be missing `companyMSService` (cleared by
  // clearOnUnrender when its anchor hid it) and `contact` (cleared by a
  // sibling's clearOtherOnChanged). Return the idiom's intended falsey instead.
  //
  // Keyed on `default === null` because that is the idiom's exact signature, so
  // no other field's clearing changes. Same test
  // `compensateForClearableClearSettingWrongFalseyValue` already uses to avoid
  // interfering with it.
  if (ifield.start === undefined && ifield.default === null) {
    console.debug(`${fn} <<<<<< returning null (default-null idiom)`)
    return null
  }
  if (ifield.start === undefined) {
    console.debug(`${fn} <<<<<< returning undefined`)
    return undefined
  }
  if (ifield.start === null) {
    // typeof null was returning 'object'; not what we want
    console.debug(`${fn} <<<<<< returning null`)
    return null
  }
  if (typeof ifield.start === 'boolean') {
    console.debug(`${fn} <<<<<< returning undefined`)
    return undefined
  }
  if (typeof ifield.start === 'number') {
    console.debug(`${fn} <<<<<< returning null`)
    return null
  }
  if (typeof ifield.start === 'string') {
    console.debug(`${fn} <<<<<< returning string`)
    // return undefined
    return '' // Not sure if this will work everywhere. Just found instance where returning undefined doesn't work. JR.
  }
  if (typeof ifield.start === 'object') {
    console.debug(`${fn} <<<<<< returning null`)
    return null
  }
  throw new Error(`${fn} <<<<<< NO RETURN. TYPEOF is ${typeof ifield.start}`)
}

export async function getClearFieldValue({
  route,
  ifield,
  entityParentObjectForCreate,
  inputFields,
  property,
}: {
  route: Route
  ifield: IEntityMaskData
  entityParentObjectForCreate?: IEntityParentObjectForCreate
  inputFields: TEntityMaskItem[]
  property: string
}): Promise<TEntityMaskDataValueTypes> {
  if (property === 'disabled' && ifield.default) {
    return await getInputFieldDefaultValue({
      route,
      ifield,
      entityParentObjectForCreate,
      inputFields,
    })
  }
  return getClearFieldValueFromStart(ifield)
}
